import { format } from 'date-fns';
import { 
  Sheet, 
  SheetContent, 
  SheetHeader, 
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  CheckCircle2, 
  AlertTriangle, 
  XCircle, 
  Send,
  Clock,
  ArrowRight,
  FileText,
  Shield,
  Activity,
  AlertCircle,
  Zap,
  Copy,
  ChevronRight
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { EnhancedClaim } from '@/lib/claimsTrackerData';
import { toast } from '@/hooks/use-toast';

interface ClaimIntelligenceDrawerProps {
  claim: EnhancedClaim | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ClaimIntelligenceDrawer({ claim, open, onOpenChange }: ClaimIntelligenceDrawerProps) {
  if (!claim) return null;

  const handleCopyClaimId = () => {
    navigator.clipboard.writeText(claim.id);
    toast({
      title: 'Copied',
      description: 'Claim ID copied to clipboard',
    });
  };

  const handleFinalizeTransmit = () => {
    toast({
      title: 'Claim Transmitted',
      description: `${claim.id} has been finalized and transmitted to ${claim.payer}`,
    });
    onOpenChange(false);
  };

  const getStatusBadge = (status: string) => {
    const configs: Record<string, { color: string; icon: React.ReactNode }> = {
      paid: { color: 'bg-success/20 text-success border-success/30', icon: <CheckCircle2 className="w-3 h-3" /> },
      submitted: { color: 'bg-primary/20 text-primary border-primary/30', icon: <Send className="w-3 h-3" /> },
      pending: { color: 'bg-warning/20 text-warning border-warning/30', icon: <Clock className="w-3 h-3" /> },
      denied: { color: 'bg-destructive/20 text-destructive border-destructive/30', icon: <XCircle className="w-3 h-3" /> },
      coding: { color: 'bg-chart-2/20 text-chart-2 border-chart-2/30', icon: <Activity className="w-3 h-3" /> },
      denial_check: { color: 'bg-chart-3/20 text-chart-3 border-chart-3/30', icon: <Shield className="w-3 h-3" /> },
      auto_fix: { color: 'bg-chart-4/20 text-chart-4 border-chart-4/30', icon: <Zap className="w-3 h-3" /> },
    };
    const config = configs[status] || configs.pending;
    return (
      <Badge variant="outline" className={cn('gap-1 border', config.color)}>
        {config.icon}
        {status.replace('_', ' ').toUpperCase()}
      </Badge>
    );
  };

  const getCheckIcon = (status: 'passed' | 'warning' | 'failed') => {
    switch (status) {
      case 'passed':
        return <CheckCircle2 className="w-4 h-4 text-success shrink-0" />;
      case 'warning':
        return <AlertTriangle className="w-4 h-4 text-warning shrink-0" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-destructive shrink-0" />;
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-2xl bg-card border-l border-border p-0 overflow-hidden">
        <ScrollArea className="h-full">
          <div className="p-6 space-y-6">
            {/* Header */}
            <SheetHeader className="space-y-4">
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <SheetTitle className="text-xl font-bold tracking-tight">
                    Claim Intelligence Report
                  </SheetTitle>
                  <SheetDescription asChild>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm text-muted-foreground">{claim.id}</span>
                      <button 
                        onClick={handleCopyClaimId}
                        className="p-1 hover:bg-muted rounded transition-colors"
                      >
                        <Copy className="w-3 h-3 text-muted-foreground" />
                      </button>
                    </div>
                  </SheetDescription>
                </div>
                {getStatusBadge(claim.status)}
              </div>
              
              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-muted/50 rounded-lg p-3 border border-border/50">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Confidence</p>
                  <p className={cn(
                    "text-lg font-bold font-mono",
                    claim.confidenceScore >= 95 ? "text-success" : 
                    claim.confidenceScore >= 90 ? "text-warning" : "text-destructive"
                  )}>
                    {claim.confidenceScore.toFixed(1)}%
                  </p>
                </div>
                <div className="bg-muted/50 rounded-lg p-3 border border-border/50">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Expected</p>
                  <p className="text-lg font-bold font-mono text-foreground">
                    ${claim.expectedAmount.toLocaleString()}
                  </p>
                </div>
                <div className="bg-muted/50 rounded-lg p-3 border border-border/50">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Provider</p>
                  <p className={cn(
                    "text-sm font-semibold truncate",
                    claim.provider === 'Dr. Larkin' && "text-primary"
                  )}>
                    {claim.provider}
                  </p>
                </div>
              </div>
            </SheetHeader>

            <Separator className="bg-border/50" />

            {/* Drift Alerts */}
            {claim.driftAlerts.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-warning" />
                  <h3 className="text-sm font-semibold text-warning">Payer Policy Drift Detected</h3>
                </div>
                {claim.driftAlerts.map((alert, i) => (
                  <div key={i} className="bg-warning/5 border border-warning/20 rounded-lg p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-warning">{alert.payer} — {alert.changeType}</span>
                      <span className="text-[10px] text-muted-foreground">Effective: {alert.effectiveDate}</span>
                    </div>
                    <p className="text-sm text-foreground">{alert.impact}</p>
                    <div className="flex items-center gap-2 text-xs text-success">
                      <CheckCircle2 className="w-3 h-3" />
                      <span>AI Response: {alert.suggestedAction}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Coding Analysis Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-muted-foreground" />
                <h3 className="text-sm font-semibold">Coding Analysis</h3>
              </div>

              {/* Raw Note */}
              <div className="bg-muted/30 rounded-lg p-4 border border-border/50">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Raw Clinical Note (Excerpt)</p>
                <p className="text-sm text-muted-foreground italic leading-relaxed">
                  "{claim.rawNoteExcerpt}"
                </p>
              </div>

              {/* Code Diff */}
              {claim.codeCorrections.length > 0 ? (
                <div className="space-y-3">
                  <p className="text-xs text-muted-foreground">AI-Suggested Code Corrections:</p>
                  {claim.codeCorrections.map((correction, i) => (
                    <div key={i} className="bg-muted/30 rounded-lg p-4 border border-border/50 space-y-3">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm line-through text-destructive/80 bg-destructive/10 px-2 py-0.5 rounded">
                            {correction.before}
                          </span>
                          <ArrowRight className="w-4 h-4 text-muted-foreground" />
                          <span className="font-mono text-sm text-success bg-success/10 px-2 py-0.5 rounded">
                            {correction.after}
                          </span>
                        </div>
                        <Badge variant="outline" className="text-[10px]">
                          {correction.type.toUpperCase()}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">{correction.reason}</p>
                      {correction.ncciReference && (
                        <p className="text-[10px] text-primary font-mono">{correction.ncciReference}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center gap-2 text-sm text-success">
                  <CheckCircle2 className="w-4 h-4" />
                  <span>No code corrections needed — original codes optimized</span>
                </div>
              )}

              {/* Final Codes */}
              <div className="bg-muted/50 rounded-lg p-4 border border-border/50">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-3">AI-Verified Codes</p>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-[10px] text-muted-foreground mb-1">CPT</p>
                    <div className="flex flex-wrap gap-1">
                      {claim.aiSuggestedCodes.cpt.map(code => (
                        <Badge key={code} className="font-mono text-xs bg-primary/20 text-primary border-0">
                          {code}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-[10px] text-muted-foreground mb-1">ICD-10</p>
                    <div className="flex flex-wrap gap-1">
                      {claim.aiSuggestedCodes.icd10.map(code => (
                        <Badge key={code} className="font-mono text-xs bg-chart-2/20 text-chart-2 border-0">
                          {code}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-[10px] text-muted-foreground mb-1">Modifiers</p>
                    <div className="flex flex-wrap gap-1">
                      {claim.aiSuggestedCodes.modifiers.map(mod => (
                        <Badge key={mod} className="font-mono text-xs bg-chart-3/20 text-chart-3 border-0">
                          {mod}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <Separator className="bg-border/50" />

            {/* Payer Simulation Log */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-muted-foreground" />
                <h3 className="text-sm font-semibold">Payer Simulation Log</h3>
                <Badge variant="outline" className="ml-auto text-[10px]">{claim.payer}</Badge>
              </div>

              <div className="space-y-2">
                {claim.payerSimulationChecks.map((check, i) => (
                  <div 
                    key={i} 
                    className={cn(
                      "flex items-start gap-3 p-3 rounded-lg border",
                      check.status === 'passed' && "bg-success/5 border-success/20",
                      check.status === 'warning' && "bg-warning/5 border-warning/20",
                      check.status === 'failed' && "bg-destructive/5 border-destructive/20"
                    )}
                  >
                    {getCheckIcon(check.status)}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-medium">{check.name}</p>
                        {check.policyRef && (
                          <span className="text-[10px] font-mono text-muted-foreground shrink-0">
                            {check.policyRef}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{check.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <Separator className="bg-border/50" />

            {/* Agent Activity Timeline */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-muted-foreground" />
                <h3 className="text-sm font-semibold">Agent Activity Timeline</h3>
              </div>

              <div className="relative space-y-3 pl-4">
                <div className="absolute left-1.5 top-2 bottom-2 w-px bg-border" />
                {claim.agentActions.map((action, i) => (
                  <div key={action.id} className="relative flex gap-3">
                    <div className={cn(
                      "absolute -left-2.5 w-3 h-3 rounded-full border-2 bg-card",
                      i === 0 ? "border-primary" : "border-muted-foreground"
                    )} />
                    <div className="flex-1 ml-4">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-medium">{action.action}</p>
                        <span className="text-[10px] text-muted-foreground">
                          {format(action.timestamp, 'MMM d, h:mm a')}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{action.detail}</p>
                      {action.confidence && (
                        <div className="flex items-center gap-1 mt-1">
                          <div className="w-16 h-1 bg-muted rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-primary rounded-full"
                              style={{ width: `${action.confidence * 100}%` }}
                            />
                          </div>
                          <span className="text-[10px] text-muted-foreground font-mono">
                            {(action.confidence * 100).toFixed(0)}%
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <Separator className="bg-border/50" />

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                onClick={handleFinalizeTransmit}
                className="flex-1 gap-2"
                disabled={claim.status === 'paid' || claim.status === 'submitted'}
              >
                <Send className="w-4 h-4" />
                Finalize & Transmit
              </Button>
              <Button variant="outline" className="gap-2">
                View Full Audit Log
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
